import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Wand2, Loader2, CheckCircle2, Sparkles } from "lucide-react";

/**
 * "Clean up merchant names" — one tap. Calls
 * `POST /api/merchants/ai/enrich-expenses` which sweeps up to 200 rows,
 * runs GPT 5.6 Sol on any noisy descriptors, and writes the normalised
 * brand name back onto every matching expense.
 *
 * Kept lean: no preview step. The backend NEVER overwrites an explicit
 * user category, only fills blanks, so this is a safe one-shot action.
 */
export default function CleanMerchantNamesButton({ onDone }) {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const run = async () => {
        setBusy(true);
        setResult(null);
        try {
            const { data } = await api.post("/merchants/ai/enrich-expenses", { limit: 200 });
            setResult(data);
            if ((data.cleaned || 0) > 0) {
                toast.success(`Cleaned ${data.cleaned} merchant names`);
                onDone?.();
            } else {
                toast.info("Nothing to clean — every merchant already looks good.");
            }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <Button
                variant="outline"
                onClick={() => { setOpen(true); setResult(null); }}
                className="border-amber-300 text-amber-800 hover:bg-amber-50"
                data-testid="expenses-clean-merchants-btn"
            >
                <Sparkles size={13} className="mr-1.5" />
                Clean up merchant names
            </Button>

            <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
                <DialogContent className="max-w-md" data-testid="clean-merchants-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl flex items-center gap-2">
                            <Wand2 size={18} className="text-amber-700" />
                            Clean up merchant names
                        </DialogTitle>
                        <DialogDescription className="text-[13px] text-stone-500">
                            GPT 5.6 Sol will scan up to 200 noisy descriptors
                            (like <span className="font-mono text-stone-700">AMZN MKTP US*XY</span>)
                            and replace them with clean brand names (Amazon).
                            Your explicit categories are never overwritten.
                        </DialogDescription>
                    </DialogHeader>

                    {busy && (
                        <div className="py-8 text-center text-sm text-stone-500 flex flex-col items-center gap-3">
                            <Loader2 size={22} className="animate-spin text-amber-700" />
                            Enriching merchants — usually 5–15 seconds…
                        </div>
                    )}

                    {result && !busy && (
                        <div className="py-4 space-y-2" data-testid="clean-merchants-result">
                            <div className="flex items-center gap-2 text-emerald-800">
                                <CheckCircle2 size={18} className="text-emerald-600" />
                                <span className="text-[14px] font-semibold">Done.</span>
                            </div>
                            <ul className="text-[12.5px] text-stone-600 space-y-1 pl-6 list-disc">
                                <li>Cleaned <b>{result.cleaned || 0}</b> expense rows</li>
                                <li>Unique merchants processed: <b>{result.unique_merchants || 0}</b></li>
                                <li>Served from cache: <b>{result.cache_hits || 0}</b></li>
                                <li>Fresh LLM calls: <b>{result.resolved_via_llm || 0}</b></li>
                            </ul>
                            <div className="text-[11px] text-stone-500 mt-2">
                                Every cleanup also learns a regex rule so the next matching
                                transaction is caught without hitting the LLM.
                            </div>
                        </div>
                    )}

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>
                            Close
                        </Button>
                        {!result && (
                            <Button
                                onClick={run}
                                disabled={busy}
                                className="bg-amber-600 hover:bg-amber-700 text-white"
                                data-testid="clean-merchants-run"
                            >
                                {busy ? "Running…" : "Start cleanup"}
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
